import React from 'react'
import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import { ChevronLeft, ChevronRight, Search } from 'react-feather'

import { ReactComponent as SortIcon } from '../assets/img/svg/sort.svg'
import { ReactComponent as SortUpIcon } from '../assets/img/svg/sort-up.svg'
import { ReactComponent as SortDownIcon } from '../assets/img/svg/sort-down.svg'
import { ReactComponent as ListIcon } from '../assets/img/svg/list-bullets.svg'
import { ReactComponent as GridIcon } from '../assets/img/svg/grid.svg'
import { queries } from '~/constants/mediaQuery'

import { useSearchParamsHandler } from '~/contexts/FilterContext'
import { getDefaultFiltersCount } from '~/helpers/getFilterCount'
import { useSearch } from '~/hooks/useSearch'
import DefaultFilterHandler from './Filters/Default/Handler'
import FilterButton from './FilterButton'

function LocationsTable({
  header,
  children,
  columns,
  data,
  total,
  loading,
  hasPagination,
  hasSearch,
  hasFilter,
  hasView,
  extendableComponent,
  noDataComponent,
  forceLimit,
  skeletonHeight,
  paginationLeft,
  menuItem,
}) {
  const intl = useIntl()

  const [view, setView] = React.useState('LIST')
  const [filterOn, setFilterOn] = React.useState(false)
  const [extendedRow, setExtendedRow] = React.useState(null)
  const { query, setQueries, setQuery } = useSearchParamsHandler()
  const {
    states,
    cities,
    areas,
    labels,
    locations,
    limit,
    page,
    sort,
    sortBy,
  } = query

  const { search, setSearch } = useSearch()
  const totalPages = React.useMemo(
    () => Math.ceil(total / (forceLimit || limit)),
    [total, forceLimit, limit]
  )

  const pages = React.useMemo(() => {
    const pages = []
    for (let i = 1; i <= totalPages; i++) {
      const frontPageNum =
        page < 5
          ? 5 + (5 - page)
          : page > totalPages - 4
          ? totalPages - page + 1
          : 5
      const backPageNum =
        page < 5 ? page : page > totalPages - 4 ? 9 - (totalPages - page) : 5

      if (i <= page - backPageNum) continue
      if (i >= page + frontPageNum) break
      pages.push(i)
    }
    return pages
  }, [totalPages, page])

  const [freeWidth, freeColumns] = React.useMemo(() => {
    let freeColumns = 0
    const usedWidth = (columns || []).reduce((acc, column) => {
      if (!column.width) freeColumns++
      return acc + (column.width ? parseInt(column.width) : 0)
    }, 0)
    return [100 - usedWidth, freeColumns]
  }, [columns])

  function handleSort(id) {
    const newSort = sortBy === id ? (sort === 'ASC' ? 'DESC' : 'ASC') : 'ASC'
    setQueries({
      sort: newSort,
      sortBy: id,
      page: 1,
    })
  }

  function handlePaginate(page) {
    setQuery('page', page)
  }

  function handlePageNext() {
    if (page === totalPages) return
    const nextPage = page + 1
    setQuery('page', nextPage)
  }

  function handlePagePrev() {
    if (page === 1) return
    const prevPage = page - 1
    setQuery('page', prevPage)
  }

  const filterCount = getDefaultFiltersCount({
    states: query.states,
    cities: query.cities,
    areas: query.areas,
    labels: query.labels,
    locations: query.locations,
  })

  return (
    <LocationsDiv>
      <div className="table-menu">
        <h2>
          <FormattedMessage id={header} />
        </h2>
        <div>
          <LocationMenu>
            {hasSearch && (
              <div className="input-search">
                <Search color="#8D8D8D" size={20} className="search-icon" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  type="text"
                  placeholder={intl.formatMessage({ id: 'Search on table' })}
                />
              </div>
            )}

            {hasView && (
              <>
                <button
                  className="view-button"
                  data-active={view === 'LIST'}
                  onClick={() => setView('LIST')}
                >
                  <ListIcon />
                </button>
                <button
                  className="view-button"
                  data-active={view === 'GRID'}
                  onClick={() => setView('GRID')}
                >
                  <GridIcon />
                </button>
              </>
            )}

            {hasFilter && (
              <FilterButton
                filterCount={filterCount}
                isActive={filterOn}
                onClick={() => setFilterOn(prevState => !prevState)}
              />
            )}

            {menuItem}
          </LocationMenu>
        </div>
      </div>
      <DefaultFilterHandler
        open={filterOn}
        onClose={() => setFilterOn(false)}
      />

      {Boolean(
        states.concat(cities, areas, labels, locations).slice(0, 9).length
      ) && (
        <FilterTagsDiv>
          {states
            .concat(cities, areas, labels, locations)
            .slice(0, 9)
            .map((item, index) => {
              return <div key={`tag-${index}`}>{item}</div>
            })}
          {states.concat(cities, areas, labels, locations).length > 9 && (
            <div>
              +{states.concat(cities, areas, labels, locations).length - 9}
            </div>
          )}
        </FilterTagsDiv>
      )}
      {children && <TableChildren>{children}</TableChildren>}
      {!data?.length && !loading && noDataComponent && <>{noDataComponent}</>}
      {loading && (
        <>
          <TableSkeleton height={skeletonHeight}></TableSkeleton>
          <CustomPagination>
            {hasPagination && (
              <>
                <button disabled={page === 1} onClick={handlePagePrev}>
                  <ChevronLeft
                    size="20"
                    color={page === 1 ? '#b3b1b1' : '#000'}
                  />
                </button>
                <div>
                  {pages.map(value => {
                    return (
                      <button
                        key={value}
                        onClick={() => handlePaginate(value)}
                        data-active={value === page}
                      >
                        {value}
                      </button>
                    )
                  })}
                </div>
                <button disabled={page === totalPages} onClick={handlePageNext}>
                  <ChevronRight
                    size="20"
                    color={page === totalPages ? '#b3b1b1' : '#000'}
                  />
                </button>
              </>
            )}
          </CustomPagination>
        </>
      )}
      {view === 'LIST' && Boolean(data?.length) && !loading && (
        <div className="table">
          <CustomTableScroll>
            <CustomTable>
              <Thead>
                {(columns || []).map(column => {
                  return (
                    <>
                      {column.sortable ? (
                        <ThButton
                          key={`col-${column.id}`}
                          onClick={() => handleSort(column.id)}
                          data-centered={column.centered}
                          width={
                            column.width
                              ? column.width
                              : (freeWidth / freeColumns).toFixed(2) + '%'
                          }
                        >
                          {column?.title && (
                            <FormattedMessage id={column.title} />
                          )}
                          {(!sort || sortBy !== column.id) && <SortIcon />}
                          {sort === 'ASC' && sortBy === column.id && (
                            <SortDownIcon />
                          )}
                          {sort === 'DESC' && sortBy === column.id && (
                            <SortUpIcon />
                          )}
                        </ThButton>
                      ) : (
                        <ThDiv
                          key={`col-${column.id}`}
                          data-centered={column.centered}
                          width={
                            column.width
                              ? column.width
                              : (freeWidth / freeColumns).toFixed(2) + '%'
                          }
                        >
                          {column?.title && (
                            <FormattedMessage id={column.title} />
                          )}
                        </ThDiv>
                      )}
                    </>
                  )
                })}
              </Thead>
              <Tbody>
                {(data || []).map((item, index) => {
                  const isExtended = extendedRow === `row-${index}`
                  const extend = () =>
                    setExtendedRow(isExtended ? null : `row-${index}`)

                  return (
                    <>
                      <Tr key={`row-${index}`}>
                        {(columns || []).map((column, i) => {
                          const cell = column.cell
                          const selector =
                            column.selector || (item => item[column.id])
                          const value = cell
                            ? cell(item, isExtended, extend)
                            : selector(item)

                          return (
                            <Td
                              key={`cell-${i}-${index}`}
                              data-centered={column.centered}
                              width={
                                column.width
                                  ? column.width
                                  : (freeWidth / freeColumns).toFixed(2) + '%'
                              }
                            >
                              {value}
                            </Td>
                          )
                        })}
                      </Tr>
                      <ExtendableTd data-active={isExtended}>
                        {extendableComponent &&
                          extendableComponent(item, isExtended, extend)}
                      </ExtendableTd>
                    </>
                  )
                })}
              </Tbody>
            </CustomTable>
          </CustomTableScroll>

          <CustomPagination
            style={{ justifyContent: paginationLeft ? 'start' : 'end' }}
          >
            {hasPagination && (
              <>
                <button disabled={page === 1} onClick={handlePagePrev}>
                  <ChevronLeft
                    size="20"
                    color={page === 1 ? '#b3b1b1' : '#000'}
                  />
                </button>
                <div>
                  {pages.map(value => {
                    return (
                      <button
                        key={value}
                        onClick={() => handlePaginate(value)}
                        data-active={value === page}
                      >
                        {value}
                      </button>
                    )
                  })}
                </div>
                <button disabled={page === totalPages} onClick={handlePageNext}>
                  <ChevronRight
                    size="20"
                    color={page === totalPages ? '#b3b1b1' : '#000'}
                  />
                </button>
              </>
            )}
          </CustomPagination>
        </div>
      )}
    </LocationsDiv>
  )
}

const LocationsDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 12px;
  gap: 2rem;
  padding: 2.5rem;
  background-color: #fff;
  box-shadow: 0px 2px 2px 0px rgba(0, 0, 0, 0.1);

  .table-menu {
    width: 100%;
    display: flex;
    justify-content: start;
    align-items: center;
    gap: 1rem;

    @media ${queries.less.mds} {
      flex-direction: column;
      align-items: start;
    }

    h2 {
      color: #767676;
      font-size: 1.5rem;
      font-weight: 600;
    }

    div {
      margin-left: auto;

      @media ${queries.less.mds} {
        width: 100%;
        margin-left: 0;
      }
    }
  }

  .table {
    width: 100%;
  }
`

const TableChildren = styled.div`
  width: 100%;
`

const LocationMenu = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 1rem;

  .input-search {
    position: relative;
    display: flex;
    justify-content: start;
    align-items: center;
  }

  input {
    // min-width: 300px;
    border: 1px solid #d8d8d8;
    border-radius: 6px;
    padding: 0.875rem 0.875rem 0.875rem 3rem;

    &:focus-visible {
      outline: #a6a4a4 auto 2px;
    }

    @media ${queries.less.sm} {
      width: 100%;
    }
  }

  .search-icon {
    position: absolute;
    left: 1rem;
  }

  button {
    height: 45px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.5rem 1rem;
    border: 1px solid #d8d8d8;
    border-radius: 6px;
    background-color: #fff;
    color: #2d3748;
    gap: 0.625rem;

    &:focus-visible {
      outline: #a6a4a4 auto 2px;
    }

    &:focus {
      outline: #a6a4a4 auto 2px;
    }

    &.view-button[data-active='true'] {
      border: none;
      background-color: #6f64f8;
      color: #fff;
    }
  }
`

const FilterTagsDiv = styled.div`
  width: 100%;

  div {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    color: #484848;
    background-color: #80839014;
    border: 1px solid #80839029;
    border-radius: 8px;
    padding: 0.25rem 0.75rem;
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;
  }
`

const CustomTableScroll = styled.div`
  overflow: auto;
`

const CustomTable = styled.div`
  position: relative;
  width: 100%;
  min-width: max-content;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-sizing: border-box;
`

const TableSkeleton = styled.div`
  height: 50rem;
  ${props => props.height && `height: ${props.height};`}
  width: 100%;
  background-color: #f7fafc;
  border-radius: 8px;
  animation: skeleton-loading 1s infinite alternate;

  @keyframes skeleton-loading {
    0% {
      background-color: hsl(200, 20%, 80%);
    }
    100% {
      background-color: hsl(200, 20%, 95%);
    }
  }
`

const CustomPagination = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;

  button {
    height: 2.5rem;
    width: 2.5rem;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.5rem;
    border: none;
    background-color: #ededed;
    border-radius: 999999px;
    aspect-ratio: 1/1;

    &:hover {
      background-color: #b3b1b1;
    }

    &:focus {
      outline: none;
    }

    &:focus-visible {
      outline: none;
    }

    &[data-active='true'] {
      background-color: #6f64f8;
      color: #fff;
    }

    &:disabled {
      background-color: #f7fafc;
      cursor: not-allowed;
    }
  }

  div {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #ededed;
    border-radius: 999999px;
  }
`

const Thead = styled.div`
  height: 3rem;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f7fafc;
  border-radius: 8px 8px 0 0;
`

const Tbody = styled.div`
  width: 100%;
`

const ThDiv = styled.div`
  display: flex;
  ${props => props.width && `flex: 0 0 ${props.width};`}
  justify-content: space-between;
  align-items: center;
  border: none;
  background-color: transparent;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #5e5873;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;

  svg {
    opacity: 0;
  }

  &:hover {
    svg {
      opacity: 1;
    }
  }

  &[data-centered='true'] {
    justify-content: center;
    gap: 0.5rem;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }
`

const ThButton = styled.button`
  display: flex;
  ${props => props.width && `flex: 0 0 ${props.width};`}
  justify-content: space-between;
  align-items: center;
  border: none;
  background-color: transparent;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 700;
  color: #5e5873;
  text-transform: uppercase;
  overflow: hidden;
  text-overflow: ellipsis;

  svg {
    opacity: 0;
  }

  &:hover {
    svg {
      opacity: 1;
    }
  }

  &[data-centered='true'] {
    justify-content: center;
    gap: 0.5rem;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }
`

const Tr = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #fff;

  &:hover {
    background-color: #f7fafc;
  }
`

const Td = styled.div`
  display: flex;
  ${props => props.width && `flex: 0 0 ${props.width};`}
  padding: 0.5rem 1rem;
  overflow: hidden;
  text-overflow: ellipsis;

  &[data-centered='true'] {
    justify-content: center;
    align-items: center;
  }
`

const ExtendableTd = styled.div`
  display: none;
  justify-content: center;

  &[data-active='true'] {
    display: flex;
  }
`

export default LocationsTable
